import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { Progress } from "../components/ui/progress";
import {
  Loader2, Radar, Users, UserPlus, TrendingUp, TrendingDown, HelpCircle,
  Award, Lightbulb, Target, ArrowDownRight,
} from "lucide-react";

const Stat = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="p-4 rounded-sm border border-border/60 bg-secondary/20" data-testid={testid}>
    <Icon className="h-5 w-5 text-primary mb-3" />
    <div className="font-mono text-2xl font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

export default function CommandCenter() {
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/command-center").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (ready && (!user || user.role !== "admin")) return <Navigate to="/dashboard" replace />;

  if (loading || !data)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const b = data.briefing;

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Radar className="h-6 w-6 text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Mission Control</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Beta Command Center</h1>

        {/* Commander Briefing */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-sm border border-primary/40 bg-primary/5 overflow-hidden mb-8" data-testid="commander-briefing">
          <div className="flex items-center gap-2 px-5 py-2.5 border-b border-primary/20 bg-primary/10">
            <Target className="h-4 w-4 text-primary" />
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Commander Briefing</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-5">
              <Stat testid="briefing-total-pilots" label="Total Pilots" value={b.total_pilots} icon={Users} />
              <Stat testid="briefing-new-today" label="New Today" value={b.new_pilots_today} icon={UserPlus} />
              <Stat testid="briefing-lead-conv" label="Lead → Pilot" value={`${b.lead_conversion_pct}%`}
                sub={`${b.leads_converted}/${b.leads_total} leads`} icon={TrendingUp} />
              <Stat testid="briefing-best" label="Best Module"
                value={b.best_module ? `${b.best_module.completion_pct}%` : "—"}
                sub={b.best_module?.label} icon={TrendingUp} />
              <Stat testid="briefing-worst" label="Lowest Module"
                value={b.worst_module ? `${b.worst_module.completion_pct}%` : "—"}
                sub={b.worst_module?.label} icon={TrendingDown} />
              <Stat testid="briefing-badges-week" label="Badges This Week" value={b.badges_this_week} icon={Award} />
            </div>

            {b.most_missed_question && (
              <div className="flex items-start gap-3 p-4 rounded-sm border border-accent/30 bg-accent/5 mb-4" data-testid="briefing-most-missed">
                <HelpCircle className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-1">
                    Most-Missed Question · {b.most_missed_question.lesson_title} ({b.most_missed_question.missed_count}× missed)
                  </div>
                  <p className="text-sm text-slate-200">{b.most_missed_question.question}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 p-4 rounded-sm border border-primary/30 bg-primary/10" data-testid="briefing-recommendation">
              <Lightbulb className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-primary mb-1">Recommended Action</div>
                <p className="text-sm text-slate-100 leading-relaxed">{b.recommendation}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Module completion */}
        <h2 className="font-display text-xl font-bold mb-4">Module Completion</h2>
        <div className="space-y-3 mb-8" data-testid="cc-modules">
          {data.modules.map((m) => (
            <div key={m.module_id} className="flex items-center gap-4 p-4 rounded-sm border border-border/70 bg-card" data-testid={`cc-module-${m.module_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-medium text-sm">{m.label}</span>
                  <span className="font-mono text-xs text-primary">{m.completion_pct}%</span>
                </div>
                <Progress value={m.completion_pct} className="h-1.5 bg-secondary" />
                <div className="text-xs font-mono text-muted-foreground mt-1.5">
                  {m.finishers} finished · {m.starters} started · {m.lessons} lessons
                </div>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-4 p-4 rounded-sm border border-border/70 bg-card" data-testid="cc-lesson1">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="font-medium text-sm">First Lesson (Activation)</span>
                <span className="font-mono text-xs text-primary">{data.lesson1_completion.pct}%</span>
              </div>
              <Progress value={data.lesson1_completion.pct} className="h-1.5 bg-secondary" />
              <div className="text-xs font-mono text-muted-foreground mt-1.5">{data.lesson1_completion.count} pilots completed Lesson 1</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Golden Five drop-off */}
          <div>
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <ArrowDownRight className="h-5 w-5 text-accent" /> Golden Five Drop-off
            </h2>
            <div className="p-5 rounded-sm border border-border/70 bg-card" data-testid="cc-dropoff">
              {data.dropoff.map((d) => {
                const top = data.dropoff[0]?.completed || 0;
                const w = top ? Math.round((d.completed / top) * 100) : 0;
                return (
                  <div key={d.lesson} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between text-xs font-mono mb-1">
                      <span className="uppercase">{d.lesson}</span>
                      <span className="text-muted-foreground">{d.completed}</span>
                    </div>
                    <div className="h-2 rounded-sm bg-secondary overflow-hidden">
                      <div className="h-full bg-primary/70" style={{ width: `${w}%` }} />
                    </div>
                  </div>
                );
              })}
              {data.biggest_drop && (
                <p className="text-xs text-accent mt-4">
                  Biggest drop: {data.biggest_drop.from} → {data.biggest_drop.to} ({data.biggest_drop.drop} pilots)
                </p>
              )}
            </div>
          </div>

          {/* Quiz + badges */}
          <div>
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" /> Quizzes & Badges
            </h2>
            <div className="p-5 rounded-sm border border-border/70 bg-card space-y-4" data-testid="cc-quiz-badges">
              <div className="grid grid-cols-3 gap-3">
                <Stat testid="cc-pass-rate" label="Pass Rate" value={`${data.quiz.overall_pass_rate}%`} icon={Target} />
                <Stat testid="cc-quizzed" label="Quizzed" value={data.quiz.students_quizzed} icon={Users} />
                <Stat testid="cc-attempts" label="Attempts" value={data.quiz.total_attempts} icon={TrendingUp} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {data.badges.map((bd) => {
                  const Icon = getIcon(bd.icon);
                  return (
                    <div key={bd.id} className="flex items-center gap-3 p-3 rounded-sm border border-border/60 bg-secondary/20" data-testid={`cc-badge-${bd.id}`}>
                      <Icon className="h-5 w-5 text-primary shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{bd.name}</div>
                        <div className="font-mono text-lg font-bold leading-none mt-0.5">{bd.earned_count}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
